import React from 'react';
import { buildClassName } from 'utils/build-class-name';
import DetailsTable from 'components/pages/address/public/components/details-table';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import styles from './style.module.scss';
import { getApproxAge } from 'utils/approx-age';
import { TryScrambleText } from 'utils/text-scrambler';
import { testIds, listingPageModalIds } from 'constants/test-constants';
import { sanitizeTestid } from 'utils/stringSanitization';
import ListingAgent, { ListingAgentInfo } from '../listing-agent';
import { createPropertyFields, isEmptyFieldValue, PropertyFields } from '../../types';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { getKeyFactsMlsNumberLabelKey } from 'ui/mls-info/format-mls-info';

//#region Types

export type ListingDetails = {
  mlsNum: string;
  /** Feed the listing came from — decides the listing-number row label. */
  providerId?: number | null;
  isCondo: boolean;
  isRental: boolean;
  isAvailable: boolean;
  availableAt: string;
  approxAge: string;
  daysOnMarket: number;
  lotSize: string;
  maintenanceFees: number;
  taxes: number;
  priceBySqft: number | null;
  type: string;
  levels: string;
  sqftLabel: string;
  garage: string;
};

type KeyFactsProps = {
  /** The listing data to be used for the key facts. */
  listingDetails: ListingDetails;
  /** The address of the property. */
  address: string;
  /** Whether the key facts are private. Passing true will blur the key facts. Defaults to false. */
  isPrivate?: boolean;
  /** Whether the key facts should be scrambled. Defaults to false. */
  shouldScramble?: boolean;
} & {
  /** Temporary label for maintenance fees. This should be removed when we have both en-us and en-ca translations. */
  localizedMaintenanceFeesLabel?: string;
} & {
  /** The listing agent information. */
  listingAgent?: {
  /** The exp agent id of the listing agent. */
    expAgentId?: number;
    /** Whether to show the military details. Defaults to false. */
    showMilitaryDetails?: boolean;
    /** The function to be called when the contact agent button is clicked. */
  };
  onContactAgentClick?: (agentInfo: ListingAgentInfo) => void;
  /** Whether to hide the square footage. Defaults to false. */
  hideSquareFootage?: boolean;
  /** When false, the Property Address row is omitted (hidden-address listings). Defaults to true. */
  showPropertyAddress?: boolean;
  /** When true, rows whose value is unavailable (null/empty) are omitted instead of showing a dash. Defaults to false. */
  hideEmptyFields?: boolean;
  /**
   * When true, the footer (Property Address, disclaimer and listing agent) is not rendered here so it can be
   * placed later by the caller (e.g. after merged detail fields). Render it with `KeyFactsFooter`. Defaults to false.
   */
  hideFooter?: boolean;
};

//#endregion

const BASE_KEY_FACTS_FIELDS = [
  `type#${LOCALE_COMMON_PATHS.ADDRESS_KEY_FACTS_TYPE}`,
  `levels#${LOCALE_COMMON_PATHS.ADDRESS_KEY_FACTS_LEVELS}`,
  `sqftLabel#${LOCALE_COMMON_PATHS.ADDRESS_KEY_FACTS_SQFT_LABEL}`,
  `garage#${LOCALE_COMMON_PATHS.ADDRESS_KEY_FACTS_GARAGE}`,
];

//#region Sub Components
function DetailsSection({ detailsFields, shouldScramble, hideSquareFootage = false }: { detailsFields: PropertyFields; shouldScramble: boolean; hideSquareFootage?: boolean }) {
  const { t } = useTranslation(Namespaces.COMMON);
  const isSquareFootageField = (label: string) => label === LOCALE_COMMON_PATHS.ADDRESS_KEY_FACTS_SQFT_LABEL || label === LOCALE_COMMON_PATHS.ADDRESS_KEY_FACTS_AVG_PRICE_PER_SQFT;
  return (
    <section>
      {detailsFields.filter(Boolean).map((field, index) => (
        <div key={index}>
          <span>{t(field.label)}</span>
          &nbsp;
          <span data-testid={sanitizeTestid(field.label) + testIds.keyFacts}>{hideSquareFootage && isSquareFootageField(field.label) ? '-' : TryScrambleText(field.value, shouldScramble)}</span>
        </div>
      ))}
    </section>
  );
}

function PropertyAddress({ address, isPrivate = false, shouldScramble = false }: { address: string; isPrivate?: boolean; shouldScramble?: boolean }) {
  const { t } = useTranslation(Namespaces.COMMON);
  return (
    <p className={buildClassName((isPrivate) && 'blur', styles.address)} data-testid={listingPageModalIds.keyFactsPropertyAddress}>
      <span>{t(LOCALE_COMMON_PATHS.ADDRESS_KEY_FACTS_PROPERTY_ADDRESS)}:</span>&nbsp;
      {TryScrambleText(address, shouldScramble)}
    </p>
  );
}

type KeyFactsFooterProps = {
  address: string;
  isPrivate?: boolean;
  shouldScramble?: boolean;
  showPropertyAddress?: boolean;
  listingAgent?: KeyFactsProps['listingAgent'];
  onContactAgentClick?: (agentInfo: ListingAgentInfo) => void;
};

/**
 * The Property Address, square-footage disclaimer and listing-agent card that normally follow the key facts.
 * Exported so callers can render it after additional content (e.g. merged description detail fields).
 */
export function KeyFactsFooter({ address, isPrivate = false, shouldScramble = false, showPropertyAddress = true, listingAgent, onContactAgentClick }: KeyFactsFooterProps) {
  const { t } = useTranslation(Namespaces.COMMON);
  return (
    <div className={buildClassName(styles.component, styles.footer)}>
      <DetailsTable isPrivate={isPrivate}>
        {showPropertyAddress && (
          <PropertyAddress address={address} isPrivate={isPrivate} shouldScramble={shouldScramble} />
        )}
        <p className={styles.disclaimer}>{t(LOCALE_COMMON_PATHS.ADDRESS_KEY_FACTS_DISCLAIMER)}</p>
      </DetailsTable>
      {listingAgent?.expAgentId &&
        <ListingAgent
          expAgentId={listingAgent.expAgentId}
          showMilitaryDetails={listingAgent.showMilitaryDetails}
          onContactAgentClick={onContactAgentClick}
        />
      }
    </div>
  );
}
//#endregion

//#region Component
export default function KeyFacts({ listingDetails, address, isPrivate = false, shouldScramble = false, localizedMaintenanceFeesLabel = 'Maintenance Fees', listingAgent, onContactAgentClick, hideSquareFootage = false, showPropertyAddress = true, hideEmptyFields = false, hideFooter = false }: KeyFactsProps) {
  const { t } = useTranslation(Namespaces.COMMON);
  const { formatCurrency } = useI18nFormatting();
  const { isCondo, availableAt, isAvailable, daysOnMarket, lotSize, mlsNum, maintenanceFees, taxes, isRental, approxAge, priceBySqft, providerId } = listingDetails;
  const taxesAsCurrency = taxes ? formatCurrency(taxes) : null;
  const maintenanceFeesAsCurrency = maintenanceFees ? formatCurrency(maintenanceFees) : null;
  const priceBySqftFormatted = priceBySqft ? formatCurrency(priceBySqft) : null;
  const keyFacts = createPropertyFields(listingDetails, t, ...BASE_KEY_FACTS_FIELDS);

  keyFacts.push(...[
    !isRental && isCondo ? { label: localizedMaintenanceFeesLabel, value: maintenanceFeesAsCurrency } : null,
    !isRental ? { label: LOCALE_COMMON_PATHS.ADDRESS_KEY_FACTS_TAXES, value: taxesAsCurrency } : null,
    isRental ? { label: LOCALE_COMMON_PATHS.ADDRESS_KEY_FACTS_DATE_AVAILABLE, value: availableAt } : null,
    isAvailable && daysOnMarket != null ? { label: LOCALE_COMMON_PATHS.ADDRESS_KEY_FACTS_DAYS_ACTIVE, value: daysOnMarket } : null,
    !isCondo ? { label: LOCALE_COMMON_PATHS.ADDRESS_KEY_FACTS_LOT_SIZE, value: lotSize } : null,
    { label: LOCALE_COMMON_PATHS.ADDRESS_KEY_FACTS_APPROX_AGE, value: approxAge ? getApproxAge(approxAge?.toString()) : null },
    !isRental && priceBySqft ? { label: LOCALE_COMMON_PATHS.ADDRESS_KEY_FACTS_AVG_PRICE_PER_SQFT, value: priceBySqftFormatted } : null,
    { label: getKeyFactsMlsNumberLabelKey(providerId), value: mlsNum || '' },
  ]);

  const visibleKeyFacts = hideEmptyFields
    ? keyFacts.filter(field => field && !isEmptyFieldValue(field.value))
    : keyFacts;

  return (
    <div className={styles.component}>
      <DetailsTable isPrivate={isPrivate}>
        <DetailsSection detailsFields={visibleKeyFacts} shouldScramble={shouldScramble} hideSquareFootage={hideSquareFootage} />
        {!hideFooter && showPropertyAddress && (
          <PropertyAddress address={address} isPrivate={isPrivate} shouldScramble={shouldScramble} />
        )}
        {!hideFooter && <p className={styles.disclaimer}>{t(LOCALE_COMMON_PATHS.ADDRESS_KEY_FACTS_DISCLAIMER)}</p>}
      </DetailsTable>
      {!hideFooter && listingAgent?.expAgentId &&
        <ListingAgent
          expAgentId={listingAgent.expAgentId}
          showMilitaryDetails={listingAgent.showMilitaryDetails}
          onContactAgentClick={onContactAgentClick}
        />
      }
    </div>
  );
}
//#endregion